import React, { useEffect } from 'react';
import { useState } from 'react';
import { Alert, Col, Container, Form, Row } from 'react-bootstrap';
import { useSelector, useDispatch } from "react-redux";
import { Formik } from "formik";
import * as Yup from "yup";
import { useHistory } from 'react-router-dom';

import CustomNavbar from '../../../components/CustomNavbar';
import SidebarDashboard from '../../../components/SidebarDashboard';
import { createSurveyTemplate,
         getSurveyTemplateById,
         updateSurveyTemplate
 } from '../../../redux/survey/surver_template/actions';
import SelectSearch from '../../../components/select_search/SelectSearch';
import FormImagePreview from '../../../components/form_image_preview/FormImagePreview';
import { getUsers } from '../../../redux/auth/actions';
import { getUsersForSelectOption } from '../utils/users';
import { customTableColumns } from './searchSelectOptions';
import { addUser } from '../../../redux/auth/actions';
import CustomTabs from '../../../components/tabs/CustomTabs';
import { tabsOptions } from './tabsOptions';
import { getFilterButtonsConfig } from './filterButtonsConfig';
import FilterButtons from '../../../components/filter_buttons/FilterButtons';
import AlertFormServerErrors from '../../../components/alert_form_server_errors/AlertFormServerErrors';
import placeholderCam from '../../../images/placeholder_cam.png';

const schema = Yup.object().shape({
  name: Yup.string().required("El nombre es requerido"),
  description: Yup.string(),
  finalMessage: Yup.string(),
  photo: Yup.mixed().test('fileSize', 
        '**El archivo es demasiado grande, es requerido un archivo menor o igual a 1 megabytes',
        value => value === null || (value && (value.size / 1024 < 1024))),
  survey_type: Yup.string().required("El tipo de encuesta es requerido"),
  paginationQuestion: Yup.string().required("La paginación de preguntas es requerida"),
  showProgressBar: Yup.string().required("La barra de progreso es requerida"),
  accessMode: Yup.string().required("El modo de acceso es requerido"),
  isNeededLogin: Yup.boolean().required("El login es requerido"),
  limitAttempts: Yup.string().when('survey_type', {
    is: 'Evaluación',
    then: Yup.string().required("El límite de intentos es requerido"),
  }),
  total_score: Yup.string().when('survey_type', {
    is: 'Evaluación',
    then: Yup.string().required("El puntaje total es requerido"),
  }),
  allowSkippingQuestions: Yup.boolean().required("Permitir saltar preguntas es requerido"),
  user: Yup.string().required("El usuario es requerido"),
});

const SurveyTemplate = (props) => {
  const [showSidebar, setShowSidebar] = useState(false);
  const dispatch = useDispatch();
  const { surveyTemplate,
          error,
          isLoading,
          isFormSaving,
          formError } = useSelector(state => state.surveyTemplate);
  const { users, loadingAuth: isLoadingUsers } = useSelector(state => state.auth);

  useEffect(() => {
    if (props.match.params.id) {
      dispatch(getSurveyTemplateById(props.match.params.id));
    }
  }, [props.match.params.id, dispatch]);

  useEffect(() => {
    dispatch(getUsers({
      offset: 0,
      limit: 20,
      filter: '',
    }));
  }, [dispatch]);
  
  const onShowSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const history = useHistory();

  const onSubmit = (values) => {
    const formData = new FormData();
    Object.keys(values).forEach(key => {
     formData.append(key, values[key]);
    });
    if(props.match.params.id){
      //if update and values.photo is null, delete photo from formData
      if(!values.photo){
        formData.delete('photo');
      }
      dispatch(updateSurveyTemplate(props.match.params.id, formData));
    }else{
      dispatch(createSurveyTemplate(formData, history));
    }
  }

  const usersSelectOption = getUsersForSelectOption(users);

  const handleAddUser = (user, setFieldValue) => {
    dispatch(addUser(user));
    setFieldValue('user', user.id);
  }

  const redirectToSurveyList = () => history.push('/encuestas');

  const filterButtonsConfig = getFilterButtonsConfig(isFormSaving, redirectToSurveyList);

  return (
    <>
      <CustomNavbar type={"dashboard"} onShowSidebar={onShowSidebar} />
      <SidebarDashboard show={showSidebar} onShowSidebar={onShowSidebar} />
      <div className="custom-wrapper">
        <Container fluid>
          {
            error && (
             <Alert variant="danger" className="my-3">
                {error}
             </Alert> 
            )
          }
           <Formik initialValues={{
            name: surveyTemplate.name || '',
            description: surveyTemplate.description || '',
            finalMessage: surveyTemplate.finalMessage || '',
            photo: null,
            survey_type: surveyTemplate.survey_type || 'Encuesta',
            paginationQuestion: surveyTemplate.paginationQuestion || 'Una página por pregunta',
            showProgressBar: surveyTemplate.showProgressBar || 'Porcentaje restante',
            accessMode: surveyTemplate.accessMode || 'Cualquier persona que tenga el enlace',
            isNeededLogin: surveyTemplate.isNeededLogin || false,
            limitAttempts: surveyTemplate.limitAttempts || '1',
            allowSkippingQuestions: surveyTemplate.allowSkippingQuestions || false,
            user: surveyTemplate.user || '',
            total_score: surveyTemplate.total_score || '1',
           }}  validationSchema={schema} onSubmit={onSubmit} enableReinitialize>
             {({
              handleSubmit,
              handleChange,
              setFieldValue,
              handleBlur,
              values,
              touched,
              errors,
            }) => (
              <Form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <FilterButtons buttons={filterButtonsConfig} justifyContent="justify-content-start" />
                  <hr />
                </div>
                {
                  formError && formError.fields_with_errors && (
                    <AlertFormServerErrors formError={formError} />
                  )
                }
                {
                  errors.photo && (
                    <Alert variant="danger" className="my-3">
                      {errors.photo}
                    </Alert>
                  )
                }
                <h5 className="font-weight-bold mb-3">Tipo de encuesta</h5>
                <Form.Group className="d-flex justify-items-center">
                  {  
                    ['Encuesta', 'Evaluación'].map((item, index) => (
                      <Form.Check
                        className="mr-3"
                        key={index}
                        type="radio"
                        name="survey_type"
                        onChange={e => setFieldValue('survey_type', item)}
                        checked={item === values.survey_type}
                        label={item}
                        disabled={isLoading} />
                    ))
                  }
                </Form.Group>
                <h5 className="font-weight-bold mb-3">Información de encuesta</h5>
                <Row>
                  <Col md={9}>
                    <Form.Group>
                      <Form.Label>Nombre</Form.Label>
                      <Form.Control
                        className="rounded-pill" 
                        type="text" 
                        name="name" 
                        placeholder="Ingrese el nombre"
                        value={values.name} 
                        onChange={handleChange} 
                        onBlur={handleBlur}
                        isInvalid={ touched.name && errors.name }
                        disabled={isLoading} />
                      <Form.Control.Feedback type="invalid" className="pl-2">
                        {errors.name}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <SelectSearch
                      endpoint="/api/app/User/"
                      searchColumns={customTableColumns}
                      isLoading={isLoadingUsers && isLoading} 
                      options={surveyTemplate && surveyTemplate.usuario ? [...usersSelectOption, { value: surveyTemplate.usuario.id, label: `${surveyTemplate.usuario.first_name} ${surveyTemplate.usuario.last_name}` }] : usersSelectOption}
                      labelName="Responsable" 
                      id="user" 
                      name="user"
                      addOption={(user) => handleAddUser(user, setFieldValue)}
                      values={values}
                      handleChange={handleChange}
                      isFormik={true}
                      error={touched.user && errors.user ? errors.user : null} />
                  </Col>
                  <Col md={3}>
                    <FormImagePreview 
                      accept="image/*" 
                      required={false} 
                      name="photo"
                      id="photo"
                      setFieldValue={setFieldValue}
                      errors={errors}
                      defaultImage={surveyTemplate.photo ? `${process.env.REACT_APP_URL}/media/${surveyTemplate.photo}` : placeholderCam} />
                  </Col>
                </Row>
                <CustomTabs 
                  defaultActiveKey={props.match.params.id ? 'questions' : 'description'}
                  id="surveyTemplateOptions"
                  tabs={tabsOptions(values, handleChange, setFieldValue, errors, props.match.params.id, isLoading)} />
              </Form>
            )}
          </Formik>
        </Container>
      </div>
    </>
  )
}

export default SurveyTemplate;